import React from 'react'
import { SkeletonComponent } from '@syncfusion/ej2-react-notifications'
import { TableSkeleton, type TableSkeletonColumn } from '@components/ui'
import './InsuranceSkeleton.css'

type InsuranceSkeletonVariant = 'details' | 'coverage'

type InsuranceSkeletonProps = {
  variant?: InsuranceSkeletonVariant
}

const PLAN_COLUMNS: TableSkeletonColumn[] = [
  { width: '40px', kind: 'text' },
  { width: '16%', kind: 'text' },
  { width: '16%', kind: 'text' },
  { width: '10%', kind: 'text' },
  { width: '12%', kind: 'text' },
  { width: '12%', kind: 'text' },
  { width: '12%', kind: 'text' },
  { width: '8%', kind: 'badge' },
  { width: '10%', kind: 'actions' },
]

const PLAN_DETAILS_COLUMNS: TableSkeletonColumn[] = [
  { width: '16px', kind: 'spacer' },
  { width: '18%', kind: 'text' },
  { width: '12%', kind: 'text' },
  { width: '12%', kind: 'text' },
  { width: '12%', kind: 'text' },
  { width: '12%', kind: 'text' },
  { width: '12%', kind: 'text' },
  { width: '14%', kind: 'text' },
]

const CLAIMS_COLUMNS: TableSkeletonColumn[] = [
  { width: '10px', kind: 'spacer' },
  { width: '18%', kind: 'text' },
  { width: '16%', kind: 'text' },
  { width: '16%', kind: 'text' },
  { width: '16%', kind: 'text' },
  { width: '18%', kind: 'badge' },
]

const COVERAGE_COLUMNS: TableSkeletonColumn[] = [
  { width: '12%', kind: 'text' },
  { width: '22%', kind: 'text' },
  { width: '12%', kind: 'text' },
  { width: '12%', kind: 'text' },
  { width: '10%', kind: 'text' },
  { width: '10%', kind: 'text' },
  { width: '10%', kind: 'text' },
  { width: '12%', kind: 'text' },
]

const SectionTitle = (): React.ReactElement => (
  <div className="insurance-skeleton-title">
    <SkeletonComponent
      shape="Rectangle"
      width="140px"
      height="16px"
      shimmerEffect="Fade"
    />
  </div>
)

const CoverageSidebarSkeleton = (): React.ReactElement => (
  <div className="coverage-sidebar-skeleton">
    <div className="coverage-side-panel-heading">
      <SkeletonComponent
        shape="Rectangle"
        width="168px"
        height="16px"
        shimmerEffect="Fade"
      />
    </div>
    <div className="coverage-side-panel-controls">
      <SkeletonComponent
        shape="Rectangle"
        width="100%"
        height="32px"
        shimmerEffect="Fade"
      />
      <SkeletonComponent
        shape="Text"
        width="88px"
        height="12px"
        shimmerEffect="Fade"
      />
      <SkeletonComponent
        shape="Rectangle"
        width="100%"
        height="32px"
        shimmerEffect="Fade"
      />
    </div>
    <div className="coverage-sidebar-group">
      {Array.from({ length: 8 }).map((_, index) => (
        <SkeletonComponent
          key={`coverage-nav-${index}`}
          shape="Rectangle"
          width="100%"
          height="32px"
          shimmerEffect="Fade"
        />
      ))}
    </div>
  </div>
)

const CoverageMainSkeleton = (): React.ReactElement => (
  <div className="insurance-skeleton insurance-skeleton--coverage">
    <div className="isc-chart" />
    <div className="isc-tabs">
      {['72px', '92px', '88px'].map((width) => (
        <SkeletonComponent
          key={width}
          shape="Rectangle"
          width={width}
          height="28px"
          shimmerEffect="Fade"
        />
      ))}
    </div>
    <TableSkeleton columns={COVERAGE_COLUMNS} rows={8} showPagination />
  </div>
)

const InsuranceSkeleton: React.FC<InsuranceSkeletonProps> = ({
  variant = 'details',
}) => {
  if (variant === 'coverage') {
    return (
      <div className="section-insurance-coverage" aria-hidden="true">
        <div className="coverage-layout">
          <aside className="coverage-side-panel-wrapper">
            <div className="coverage-side-panel">
              <CoverageSidebarSkeleton />
            </div>
          </aside>
          <div className="deductions-limitations-col">
            <CoverageMainSkeleton />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="insurance-skeleton" aria-hidden="true">
      <SectionTitle />
      <TableSkeleton
        columns={PLAN_COLUMNS}
        rows={3}
        actionCount={2}
        showPagination={false}
        fillHeight={false}
      />
      <TableSkeleton
        columns={PLAN_DETAILS_COLUMNS}
        rows={1}
        showPagination={false}
        fillHeight={false}
      />
      <SectionTitle />
      <TableSkeleton
        columns={CLAIMS_COLUMNS}
        rows={8}
        showPagination
        fillHeight={false}
      />
    </div>
  )
}

export default InsuranceSkeleton
